import React, { FC } from 'react';
import { Product } from 'shared/types/product/Product';
import { hasHtmlContent, sanitize } from 'helpers/utils/htmlSanitize';
import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import ProductsLoadError from 'components/commercetools-ui/atoms/products-load-error';
import ProductTile, { ProductTileLayout } from 'components/commercetools-ui/organisms/product/product-tile';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, minitablet, mobile, smallMobile, tablet } from 'helpers/utils/screensizes';

import { SectionWithProductSliderContent } from './types';
import { useAdditionalCss } from '../helpers/useAdditionalCss';

interface ProductSliderViewProps {
  content: SectionWithProductSliderContent;
  /** Already in display order (see useProductBlock). */
  products: Product[];
  tileLayout: ProductTileLayout;
}

/**
 * "Algolia Query" slider layout: grey band, heading only when set, 4 tiles per view on tablet up.
 * Presentational only, so the tiles land in the SSR HTML.
 */
export const AlgoliaQuerySliderView: FC<ProductSliderViewProps> = ({ content, products, tileLayout }) => {
  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);

  const title = content?.title;
  const description = content?.description;
  const hasDescription = hasHtmlContent(description);
  const additional_css = content?.additional_css;
  const additional_css_class_names = content?.additional_css_class_names;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'AlgoliaQueryProductSlider');

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div
        className={`bg-[#E5E9EB] px-24 md:px-28 lg:px-48 xl:px-60 2xl:px-80 pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}
      >
        <div id="algolia-product-slider">
          {title && (
            <Typography
              className={`${hasDescription ? 'pb-16' : 'pb-32'} text-32 font-medium text-primary-black xl:text-36 2xl:text-48`}
              as="h3"
            >
              {title}
            </Typography>
          )}
          {hasDescription && (
            <div
              className="pb-32 text-16 font-normal leading-6 text-[#333] md:text-18 2xl:text-20 [&>*:first-child]:!mt-0 [&>*:last-child]:!mb-0 [&_p]:my-8"
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
            />
          )}
          <div className="product-slider-container relative w-full">
            {/* No products (nothing configured to query, or the preload failed) — the message replaces the
                carousel but stays INSIDE the section, so the heading above it still tells the visitor
                which block is broken. */}
            {products.length === 0 ? (
              <ProductsLoadError className="py-24 lg:py-40" />
            ) : (
            <Slider
              className="mb-32"
              slidesPerView={2.1}
              slidesPerGroup={1}
              dots={false}
              autoplay={false}
              arrows={isTabletSize || isDesktopSize || isMiniTabletSize}
              innerArrows
              solidArrows
              allowArrowsOnTouchDevice
              nextButtonStyles={{ top: '100%', left: 50 }}
              prevButtonStyles={{ top: '100%', left: 0 }}
              allowTouchMove
              spaceBetween={8}
              breakpoints={{
                [tablet]: { slidesPerView: 4, slidesPerGroup: 4, spaceBetween: 16 },
                [smallMobile]: { slidesPerView: 1.2, slidesPerGroup: 1, spaceBetween: 16 },
                [mobile]: { slidesPerView: 2.1, slidesPerGroup: 2, spaceBetween: 16 },
              }}
            >
              {products.map((product, index) => (
                <div key={product.productId || index} className="mb-48">
                  <ProductTile product={product} layout={tileLayout} />
                </div>
              ))}
            </Slider>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
